import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostListener, Inject, InjectionToken, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, ViewChild, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { businessOutline, chevronForwardOutline, mapOutline, peopleOutline, refreshOutline, trashOutline, warningOutline } from 'ionicons/icons';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { webSocket, WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';
import { environment } from '../../../../environments/environment';
import { SpatialService } from '../../../core/services/spatial.service';
import { MonitoringPreferencesService } from '../../../core/services/monitoring-preferences.service';
import { authenticationSessionKey, SessionRefreshService } from '../../../core/services/session-refresh.service';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { eventTypeToData } from '../../../shared/models/be/notification';
import { IRoom } from '../../../shared/models/be/room';
import { IUnit } from '../../../shared/models/be/unit';
import { SpatialFrontendConfig, SpatialPeopleSnapshot, SpatialPerson, SpatialScope, SpatialSocketMessage, SpatialStreamStatus, spatialBlockingMessage } from '../../../shared/models/be/spatial';
import { getTimeLeft } from '../../../shared/utils/transformation.util';
import { BedStatesRoomComponent } from '../../../shared/components/bed-states/bed-states-room.component';
import { SpatialPosePresentation, spatialAvatarHeading, spatialPosePresentation } from './spatial-person-pose';

type ConfigState = 'idle' | 'loading' | 'success' | 'error';
type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';
interface MapPanState { pointerId: number; startX: number; startY: number; scrollLeft: number; scrollTop: number; }
interface SpatialPersonMarker {
  person: SpatialPerson; pixelX: number; pixelY: number; leftPercent: number; topPercent: number;
  pose: SpatialPosePresentation; description: string; widthPercent: number; heading: number;
}
export type SpatialSocketFactory = (config: WebSocketSubjectConfig<unknown>) => WebSocketSubject<unknown>;
export const SPATIAL_SOCKET_FACTORY = new InjectionToken<SpatialSocketFactory>('SPATIAL_SOCKET_FACTORY', { factory: () => webSocket });

export function buildSpatialWebSocketUrl(wsBaseUrl: string, streamPath: string, accessToken: string): string {
  const base = new URL(wsBaseUrl), url = new URL(streamPath, base.origin);
  if (url.origin !== base.origin || !url.pathname.startsWith(base.pathname.replace(/\/$/, '') + '/')) throw new Error('Stream Spatial fuori dal backend configurato.');
  url.searchParams.set('token', accessToken);
  return url.href;
}

@Component({
  selector: 'app-spatial-tracking', standalone: true,
  templateUrl: './spatial-tracking.component.html', styleUrls: ['./spatial-tracking.component.scss'],
  imports: [CommonModule, FormsModule, IonIcon, IonSpinner, MatIconModule, TranslateModule, BedStatesRoomComponent]
})
export class SpatialTrackingComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild('mapStage') private mapStage?: ElementRef<HTMLElement>;
  @ViewChild('mapZoomLayer') private mapZoomLayer?: ElementRef<HTMLElement>;
  @Input({ required: true }) public scope!: SpatialScope;
  @Input() public units: IUnit[] = [];
  @Input() public showBedStates = false;
  @Output() public readonly roomSelected = new EventEmitter<IRoom>();
  @Output() public readonly configurationChanged = new EventEmitter<void>();
  @Output() public readonly accessLost = new EventEmitter<string>();
  private readonly destroy$ = new Subject<void>();
  private resources = new Subscription();
  private socket?: WebSocketSubject<unknown>;
  private socketSubscription?: Subscription;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private staleTimer?: ReturnType<typeof setTimeout>;
  private socketGeneration = 0;
  private configGeneration = 0;
  private isDestroyed = false;
  private pageHidden = false;
  private offline = navigator.onLine === false;
  private suspended = false;
  private sessionKey: string | null = null;
  private runtimeEpoch: string | null = null;
  private authenticationRetried = false;
  private sequence = -1;
  private avatarHeadings = new Map<string, number>();
  private imageUrl: string | null = null;
  private mapPanState: MapPanState | null = null;
  private readonly loadedMapAspectRatio = signal<number | null>(null);
  public configState: ConfigState = 'idle';
  public configMessage = 'Configurazione non ancora richiesta.';
  public config: SpatialFrontendConfig | null = null;
  public restError: string | null = null;
  public connectionState: ConnectionState = 'disconnected';
  public connectionMessage = 'Stream non ancora connesso.';
  public websocketError: string | null = null;
  public streamStatus: SpatialStreamStatus = 'NOT_READY';
  public showDebugInformation = false;
  public showMarkerDetails = false;
  public readonly mapZoomMin = 1;
  public readonly mapZoomMax = 3;
  public readonly mapZoomStep = 0.25;
  public mapZoom = 1;
  public isMapPanning = false;
  public totalMessages = 0;
  public lastPayload: SpatialSocketMessage | null = null;
  public latestSnapshot: SpatialPeopleSnapshot | null = null;
  public messageLog: { receivedAt: Date; payload: SpatialSocketMessage; }[] = [];
  public readonly eventTypeToData = eventTypeToData;
  constructor(
    private readonly api: SpatialService, private readonly requests: RequestsService,
    private readonly refresh: SessionRefreshService,
    @Inject(SPATIAL_SOCKET_FACTORY) private readonly socketFactory: SpatialSocketFactory,
    @Optional() private readonly monitoringPreferences: MonitoringPreferencesService | null = null
  ) {
    addIcons({ 'map-outline': mapOutline, 'people-outline': peopleOutline, 'refresh-outline': refreshOutline,
      'business-outline': businessOutline, 'chevron-forward-outline': chevronForwardOutline, 'trash-outline': trashOutline, 'warning-outline': warningOutline });
  }
  ngOnChanges(changes: SimpleChanges): void { if (changes['scope']) this.reconnect(); }
  ngOnInit(): void {
    this.requests.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(() => this.verifySession());
  }
  ngOnDestroy(): void { this.isDestroyed = true; this.reset(); this.destroy$.next(); this.destroy$.complete(); }
  @HostListener('document:visibilitychange')
  public onVisibilityChange(): void { this.updatePageActivity(); }
  @HostListener('window:pagehide')
  public onPageHide(): void { this.pageHidden = true; this.updatePageActivity(); }
  @HostListener('window:pageshow')
  public onPageShow(): void { this.pageHidden = false; this.updatePageActivity(); }
  @HostListener('window:offline')
  public onOffline(): void { this.offline = true; this.updatePageActivity(); }
  @HostListener('window:online')
  public onOnline(): void { this.offline = false; this.updatePageActivity(); }
  @HostListener('window:storage', ['$event'])
  public onSessionStorage(event: StorageEvent): void {
    if (event.key === null || ['USER', 'ACCESS_TOKEN', 'WITA_LOG_CONTEXT'].includes(event.key)) {
      if (!localStorage.getItem('ACCESS_TOKEN')) this.revokeAccess();
      else this.verifySession();
    }
  }
  private get pageActive(): boolean {
    return !this.isDestroyed && !this.pageHidden && !this.offline && document.visibilityState !== 'hidden';
  }
  private updatePageActivity(): void {
    if (!this.pageActive) {
      if (!this.suspended) { this.suspended = true; this.reset(); }
    } else if (this.suspended) {
      this.suspended = false;
      this.reconnect();
    }
  }
  private verifySession(): void {
    if (this.isDestroyed) return;
    if (!this.authorized) this.revokeAccess();
    else if (this.sessionKey !== null && this.sessionKey !== authenticationSessionKey()) this.reconnect();
  }
  public get authorized(): boolean {
    const user = this.requests.getCurrentUser();
    return !!this.scope && user?.role === 'superuser' && user.customer?.spatialTrackingEnabled === true
      && user.customer.id === this.scope.customerId && user.building?.id === this.scope.buildingId;
  }
  public get unitsWithSensors(): IUnit[] {
    return this.units.filter(unit => unit.id === this.scope?.unitId).flatMap(unit => {
      const rooms = unit.rooms?.filter(room => !!room.devices?.length) || [];
      return rooms.length ? [{ ...unit, rooms }] : [];
    });
  }
  public get roomCount(): number { return this.unitsWithSensors.reduce((sum, unit) => sum + (unit.rooms?.length || 0), 0); }
  public selectRoom(room: IRoom): void { this.roomSelected.emit(room); }
  public getTimeLeft(date: Date | string): number | string { return getTimeLeft(date); }
  public get activeInstallationId(): string { return this.config?.installation_id || ''; }
  public get calibrationSetId(): string { return this.config?.calibration_set_id || '--'; }
  public get lastSnapshotTimestampUtcMs(): number | null { return this.latestSnapshot?.timestamp_utc_ms ?? null; }
  public get currentPeople(): SpatialPerson[] { return this.authorized && this.streamStatus === 'LIVE' ? this.latestSnapshot?.people || [] : []; }
  public get mapImageUrl(): string | null { return this.authorized ? this.imageUrl : null; }
  public get mapAspectRatio(): number {
    const image = this.config?.map?.image;
    return this.loadedMapAspectRatio() ?? (image && image.width_px > 0 && image.height_px > 0 ? image.width_px / image.height_px : 1);
  }
  public get mapMarkers(): SpatialPersonMarker[] {
    const map = this.config?.map;
    if (!map || !this.mapImageUrl) return [];
    return this.currentPeople.filter(person => person.map_position_inside).map(person => {
      const pose = spatialPosePresentation(person);
      return {
        person, pose, pixelX: person.map_position_px[0], pixelY: person.map_position_px[1],
        leftPercent: person.map_position_px[0] / map.image.width_px * 100,
        topPercent: person.map_position_px[1] / map.image.height_px * 100,
        widthPercent: (64 * 1.5) / map.image.width_px * 100,
        heading: this.avatarHeadings.get(person.id) ?? 0,
        description: 'Persona ' + person.id + ', ' + pose.label + (pose.eventLabel ? ', ' + pose.eventLabel : '')
          + (person.state === 'held' ? ', ultima posa rilevata' : '')
      };
    });
  }
  public get streamMessage(): string {
    if (this.connectionState === 'error' || this.connectionState === 'disconnected') return this.connectionMessage;
    switch (this.streamStatus) {
      case 'WAITING_FOR_DATA': return 'In attesa di dati dai sensori.';
      case 'STALE': return 'Dati dei sensori non aggiornati.';
      case 'LIVE': return 'Tracking attivo';
      default: return this.configMessage;
    }
  }
  public get lastPayloadJson(): string { return this.lastPayload ? JSON.stringify(this.lastPayload, null, 2) : 'Nessun payload Spatial ricevuto.'; }
  public get connectionLabel(): string { return ({ connected: 'Connesso', connecting: 'Connessione in corso', error: 'Errore', disconnected: 'Disconnesso' })[this.connectionState]; }
  public get configLabel(): string { return ({ success: 'Caricata', loading: 'Caricamento', error: 'Errore', idle: 'In attesa' })[this.configState]; }
  public formatTimestamp(value: number | null): string { return value === null || !Number.isFinite(value) ? '--' : new Date(value).toLocaleString('it-IT'); }
  public clearLocalLog(): void { this.lastPayload = null; this.messageLog = []; }
  public onMapImageError(): void { this.releaseImage(); this.restError = 'Impossibile visualizzare il file della mappa.'; }
  public onMapImageLoad(event: Event): void {
    const image = event.target;
    if (image instanceof HTMLImageElement && image.naturalWidth > 0 && image.naturalHeight > 0) this.loadedMapAspectRatio.set(image.naturalWidth / image.naturalHeight);
  }
  public reconnect(resetAuthenticationRetry = true): void {
    this.reset();
    if (resetAuthenticationRetry) this.authenticationRetried = false;
    if (!this.pageActive) { this.suspended = true; return; }
    this.suspended = false;
    if (!this.authorized || this.isDestroyed) return;
    this.sessionKey = authenticationSessionKey();
    const generation = this.configGeneration;
    this.configState = 'loading'; this.configMessage = 'Caricamento configurazione della mappa...'; this.restError = null;
    this.resources.add(this.api.config(this.scope).subscribe({
      next: config => {
        if (!this.isCurrentConfig(generation)) return;
        if (config.schema_version !== 2 || config.unitId !== this.scope.unitId || config.buildingId !== this.scope.buildingId || config.customerId !== this.scope.customerId) {
          this.configState = 'error'; this.configMessage = 'La configurazione ricevuta non corrisponde all’unità selezionata.'; return;
        }
        this.config = config; this.configState = 'success'; this.configMessage = spatialBlockingMessage(config);
        this.streamStatus = config.stream.status; this.connectionMessage = this.configMessage;
        if (config.configurationState !== 'ACTIVE' || !config.mapUsable || !config.trackingReady || !config.image_url || !config.map
          || !config.configRevisionId || !config.mapRevisionId || !config.stream.enabled || !config.stream.path) return;
        this.resources.add(this.api.image(config.image_url).subscribe({
          next: blob => { if (this.isCurrentConfig(generation)) { this.releaseImage(); this.imageUrl = URL.createObjectURL(blob); } },
          error: error => {
            if (!this.isCurrentConfig(generation)) return;
            if (error instanceof HttpErrorResponse && error.status === 403) { this.revokeAccess('Accesso al file della mappa rifiutato dal backend (HTTP 403).'); return; }
            this.restError = 'Impossibile caricare la mappa. Ricaricare per riprovare.';
            if (error instanceof HttpErrorResponse && error.status === 409) this.scheduleReload();
          }
        }));
        this.connectSocket();
      },
      error: error => {
        if (!this.isCurrentConfig(generation)) return;
        if (error instanceof HttpErrorResponse && error.status === 403) { this.revokeAccess('Accesso alla configurazione Spatial rifiutato dal backend (HTTP 403).'); return; }
        this.configState = 'error'; this.configMessage = 'Impossibile caricare la configurazione Spatial.';
        this.restError = error instanceof HttpErrorResponse ? 'Richiesta fallita (HTTP ' + error.status + ').' : 'Configurazione non disponibile.';
      }
    }));
  }
  private connectSocket(): void {
    const path = this.config?.stream.path, token = localStorage.getItem('ACCESS_TOKEN');
    if (!path || !token || !this.authorized || !this.pageActive || this.suspended) return;
    const generation = this.socketGeneration;
    this.connectionState = 'connecting'; this.connectionMessage = 'Connessione ai sensori in corso...'; this.websocketError = null;
    try {
      this.socket = this.socketFactory({
        url: buildSpatialWebSocketUrl(environment.wsUrl, path, token),
        openObserver: { next: () => { if (this.isCurrentSocket(generation)) this.connectionMessage = 'In attesa del collegamento ai sensori.'; } },
        closeObserver: { next: event => {
          if (!this.isCurrentSocket(generation)) return;
          this.clearPeople(); this.connectionState = 'disconnected';
          if (event.code === 4003 && ['MONITORING_SELECTION_CHANGED', 'MONITORING_UNIT_EXCLUDED'].includes(event.reason)) {
            this.revokeAccess('Il piano non è più selezionato per il monitoraggio.');
            this.monitoringPreferences?.changed({ buildingId: this.scope.buildingId });
            return;
          }
          if ([1008, 4003].includes(event.code)) { this.revokeAccess('Accesso allo stream Spatial rifiutato dal backend (WebSocket ' + event.code + ').'); return; }
          // 4000 is a malformed subscription, not a revoked permission. Keep the map
          // and the server error visible, and wait for an explicit retry.
          if (event.code === 4000) {
            this.socketGeneration++; this.closeSocket();
            this.connectionState = 'error';
            this.connectionMessage = this.websocketError || 'Richiesta allo stream Spatial non valida (WebSocket 4000). Ricaricare per riprovare.';
            this.websocketError = this.connectionMessage;
            return;
          }
          if (event.code === 4001) { this.renewAccess(); return; }
          if ([4004, 4009].includes(event.code)) {
            this.reset(); this.configurationChanged.emit(); this.reconnect(false); return;
          }
          this.connectionMessage = 'Connessione interrotta. Riconnessione in corso...'; this.scheduleReload();
        } }
      });
      this.socketSubscription = this.socket.subscribe({
        next: message => { if (this.isCurrentSocket(generation)) this.handleMessage(message); },
        error: () => {
          if (!this.isCurrentSocket(generation)) return;
          this.clearPeople(); this.connectionState = 'error'; this.connectionMessage = 'Connessione ai sensori non disponibile. Riconnessione in corso...'; this.scheduleReload();
        },
        complete: () => { if (this.isCurrentSocket(generation)) this.scheduleReload(); }
      });
    } catch (error) { this.connectionState = 'error'; this.connectionMessage = error instanceof Error ? error.message : 'Stream non disponibile.'; }
  }
  private handleMessage(message: unknown): void {
    if (!isRecord(message)) return;
    if (message['type'] === 'ping') { this.socket?.next({ type: 'pong', timestamp: new Date().toISOString() }); return; }
    if (message['type'] === 'spatial_configuration_changed.v1' && message['schema_version'] === 1
      && message['buildingId'] === this.scope.buildingId && message['unitId'] === this.scope.unitId && message['previousConfigRevisionId'] === this.config?.configRevisionId) {
      this.reset(); this.configurationChanged.emit(); this.reconnect(false); return;
    }
    if (message['type'] === 'spatial_error.v1' && message['schema_version'] === 1 && (message['unitId'] === null || message['unitId'] === this.scope.unitId)) {
      this.clearPeople(); this.connectionMessage = typeof message['message'] === 'string' ? message['message'] : 'Stream non disponibile.';
      this.websocketError = this.connectionMessage;
      // The following close code distinguishes expired JWT from revoked access.
      return;
    }
    if (!this.matchesConfig(message)) return;
    if (message['type'] === 'spatial_connected.v2' && message['schema_version'] === 2 && typeof message['runtimeEpoch'] === 'string'
      && message['runtimeEpoch'].length > 0 && Number.isFinite(message['timestamp_utc_ms']) && isStreamStatus(message['streamStatus'])) {
      if (this.runtimeEpoch && this.runtimeEpoch !== message['runtimeEpoch']) { this.reset(); this.reconnect(false); return; }
      if (this.runtimeEpoch === message['runtimeEpoch']) return;
      this.clearPeople(); this.runtimeEpoch = message['runtimeEpoch']; this.sequence = -1;
      this.authenticationRetried = false;
      this.applyStatus(message['streamStatus']); this.register(message as unknown as SpatialSocketMessage); this.connectionState = 'connected'; return;
    }
    if (!this.runtimeEpoch || message['runtimeEpoch'] !== this.runtimeEpoch) return;
    if (message['type'] === 'spatial_status.v1' && message['schema_version'] === 1 && isStreamStatus(message['status'])) { this.applyStatus(message['status']); return; }
    if (message['type'] !== 'spatial_people.v2' || message['schema_version'] !== 2 || !Number.isSafeInteger(message['sequence']) || (message['sequence'] as number) < 0
      || !Number.isFinite(message['timestamp_utc_ms']) || !Number.isFinite(message['generated_at_utc_ms'])
      || (message['sequence'] as number) <= this.sequence || !Array.isArray(message['people']) || !message['people'].every(isPerson)) return;
    this.sequence = message['sequence'] as number; this.latestSnapshot = message as unknown as SpatialPeopleSnapshot;
    const headings = new Map<string, number>();
    for (const person of this.latestSnapshot.people) {
      const measured = spatialAvatarHeading(person, this.config!.map!.global_xz_to_pixel);
      const previous = this.avatarHeadings.get(person.id);
      // Smooth across the 0/360 boundary and retain the last direction when stationary or held.
      const delta = measured == null || previous == null ? 0 : ((measured - previous + 540) % 360 + 360) % 360 - 180;
      headings.set(person.id, measured == null ? previous ?? 0 : previous == null ? measured : previous + delta * .35);
    }
    this.avatarHeadings = headings;
    this.streamStatus = 'LIVE'; this.connectionState = 'connected'; this.websocketError = null; this.register(this.latestSnapshot); this.armStaleTimer();
  }
  private matchesConfig(message: Record<string, unknown>): boolean {
    const config = this.config;
    return !!config && this.authorized && message['buildingId'] === this.scope.buildingId && message['unitId'] === this.scope.unitId
      && message['installation_id'] === config.installation_id && message['calibration_set_id'] === config.calibration_set_id
      && message['configRevisionId'] === config.configRevisionId && message['mapRevisionId'] === config.mapRevisionId;
  }
  private applyStatus(status: SpatialStreamStatus): void { this.streamStatus = status; if (status !== 'LIVE') this.clearPeople(); else this.armStaleTimer(); }
  private armStaleTimer(): void {
    clearTimeout(this.staleTimer); const delay = this.config?.stream.staleAfterMs;
    if (!delay || delay < 0 || !Number.isFinite(delay)) return;
    this.staleTimer = setTimeout(() => { this.streamStatus = 'STALE'; this.clearPeople(); }, delay);
  }
  private clearPeople(): void { this.latestSnapshot = null; this.avatarHeadings.clear(); clearTimeout(this.staleTimer); this.staleTimer = undefined; }
  private register(message: SpatialSocketMessage): void {
    this.totalMessages++; this.lastPayload = message; this.messageLog = [...this.messageLog, { receivedAt: new Date(), payload: message }].slice(-50);
  }
  private scheduleReload(): void {
    if (this.reconnectTimer || !this.pageActive || this.suspended || !this.authorized) return;
    this.reconnectTimer = setTimeout(() => { this.reconnectTimer = undefined; this.reconnect(false); }, 3000);
  }
  private renewAccess(): void {
    clearTimeout(this.reconnectTimer); this.reconnectTimer = undefined;
    this.socketGeneration++; this.closeSocket();
    if (this.authenticationRetried) {
      this.reset(); this.connectionState = 'error'; this.connectionMessage = 'Accesso allo stream rifiutato. Rinnovare la sessione prima di riprovare.';
      return;
    }
    this.authenticationRetried = true;
    const generation = this.configGeneration;
    this.resources.add(this.refresh.refresh().subscribe({
      next: () => { if (this.isCurrentConfig(generation)) this.reconnect(false); },
      error: () => {
        if (!this.isCurrentConfig(generation)) return;
        this.reset(); this.connectionState = 'error'; this.connectionMessage = 'Sessione non disponibile. Rinnovare l’accesso prima di riprovare.';
      }
    }));
  }
  private revokeAccess(message = 'Accesso Spatial non disponibile per l’utente o il cliente selezionato.'): void {
    this.reset(); this.showDebugInformation = false; this.showMarkerDetails = false; this.accessLost.emit(message);
  }
  private reset(): void {
    this.configGeneration++; this.socketGeneration++; clearTimeout(this.reconnectTimer); this.reconnectTimer = undefined;
    this.resources.unsubscribe(); this.resources = new Subscription();
    this.closeSocket();
    this.releaseImage(); this.clearPeople(); this.runtimeEpoch = null; this.sequence = -1;
    this.config = null; this.configState = 'idle'; this.restError = null; this.websocketError = null;
    this.connectionState = 'disconnected'; this.connectionMessage = 'Stream non disponibile.'; this.streamStatus = 'NOT_READY';
    this.mapZoom = 1; this.mapPanState = null; this.isMapPanning = false; this.totalMessages = 0; this.clearLocalLog();
  }
  private closeSocket(): void {
    const socket = this.socket, subscription = this.socketSubscription;
    this.socket = undefined; this.socketSubscription = undefined;
    socket?.complete(); subscription?.unsubscribe();
  }
  private releaseImage(): void { if (this.imageUrl) URL.revokeObjectURL(this.imageUrl); this.imageUrl = null; this.loadedMapAspectRatio.set(null); }
  private isCurrentConfig(generation: number): boolean { return this.pageActive && !this.suspended && this.authorized && this.sessionKey === authenticationSessionKey() && generation === this.configGeneration; }
  private isCurrentSocket(generation: number): boolean { return this.pageActive && !this.suspended && this.authorized && this.sessionKey === authenticationSessionKey() && generation === this.socketGeneration; }
  public zoomIn(): void {
    this.setMapZoomAtPoint(this.mapZoom + this.mapZoomStep);
  }

  public zoomOut(): void {
    this.setMapZoomAtPoint(this.mapZoom - this.mapZoomStep);
  }

  public resetMapZoom(): void {
    this.setMapZoomAtPoint(1);
  }

  public onMapWheel(event: WheelEvent): void {
    event.preventDefault();

    const container = event.currentTarget;
    if (!(container instanceof HTMLElement)) {
      return;
    }

    const bounds = container.getBoundingClientRect();
    this.setMapZoomAtPoint(
      this.mapZoom + (event.deltaY < 0 ? this.mapZoomStep : -this.mapZoomStep),
      container,
      event.clientX - bounds.left,
      event.clientY - bounds.top
    );
  }

  public startMapPan(event: PointerEvent): void {
    const container = event.currentTarget;

    if (
      !(container instanceof HTMLElement) ||
      event.button !== 0 ||
      (container.scrollWidth <= container.clientWidth && container.scrollHeight <= container.clientHeight)
    ) {
      return;
    }

    event.preventDefault();
    container.setPointerCapture(event.pointerId);
    this.mapPanState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop
    };
    this.isMapPanning = true;
  }

  public moveMapPan(event: PointerEvent): void {
    const container = event.currentTarget;
    const panState = this.mapPanState;

    if (!(container instanceof HTMLElement) || !panState || panState.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    container.scrollLeft = panState.scrollLeft - (event.clientX - panState.startX);
    container.scrollTop = panState.scrollTop - (event.clientY - panState.startY);
  }

  public endMapPan(event: PointerEvent): void {
    const container = event.currentTarget;

    if (!(container instanceof HTMLElement) || this.mapPanState?.pointerId !== event.pointerId) {
      return;
    }

    if (container.hasPointerCapture(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }

    this.mapPanState = null;
    this.isMapPanning = false;
  }

  public get mapZoomPercent(): number {
    return Math.round(this.mapZoom * 100);
  }


  private setMapZoomAtPoint(
    zoom: number,
    container: HTMLElement | undefined = this.mapStage?.nativeElement,
    focalX?: number,
    focalY?: number
  ): void {
    const previousZoom = this.mapZoom;
    const clampedZoom = Number(
      Math.min(this.mapZoomMax, Math.max(this.mapZoomMin, zoom)).toFixed(2)
    );

    if (clampedZoom === previousZoom) {
      return;
    }

    if (!container) {
      this.mapZoom = clampedZoom;
      return;
    }

    const anchorX = focalX ?? container.clientWidth / 2;
    const anchorY = focalY ?? container.clientHeight / 2;
    const previousScrollLeft = container.scrollLeft;
    const previousScrollTop = container.scrollTop;
    const layer = this.mapZoomLayer?.nativeElement;
    const previousOffsetX = Math.max(0, (container.clientWidth - (layer?.clientWidth ?? container.clientWidth)) / 2);
    const previousOffsetY = Math.max(0, (container.clientHeight - (layer?.clientHeight ?? container.clientHeight)) / 2);
    const scaleRatio = clampedZoom / previousZoom;

    this.mapZoom = clampedZoom;

    requestAnimationFrame(() => {
      if (this.isDestroyed) {
        return;
      }

      const offsetX = Math.max(0, (container.clientWidth - (layer?.clientWidth ?? container.clientWidth)) / 2);
      const offsetY = Math.max(0, (container.clientHeight - (layer?.clientHeight ?? container.clientHeight)) / 2);
      container.scrollLeft = (previousScrollLeft + anchorX - previousOffsetX) * scaleRatio + offsetX - anchorX;
      container.scrollTop = (previousScrollTop + anchorY - previousOffsetY) * scaleRatio + offsetY - anchorY;
    });
  }


}
function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === 'object' && value !== null; }
function isStreamStatus(value: unknown): value is SpatialStreamStatus { return ['NOT_READY', 'WAITING_FOR_DATA', 'LIVE', 'STALE'].includes(value as string); }
function isPerson(value: unknown): value is SpatialPerson {
  return isRecord(value) && typeof value['id'] === 'string' && Number.isFinite(value['x_m']) && Number.isFinite(value['z_m'])
    && Number.isFinite(value['confidence']) && ['tracked', 'held'].includes(value['state'] as string)
    && Array.isArray(value['observed_by']) && value['observed_by'].every(sensor => typeof sensor === 'string')
    && Array.isArray(value['map_position_px']) && value['map_position_px'].length === 2 && value['map_position_px'].every(Number.isFinite)
    && typeof value['map_position_inside'] === 'boolean';
}
